// Vertical reskin PROOF (roadmap Track E3.2 — "one engine, many expressions").
//
// A self-contained, OPT-IN skin layer: the same platform engine, re-themed and re-vocabularied
// for a different vertical. Activated by `?skin=<id>` (default: camp), so the production camp
// experience is byte-identical unless a skin is explicitly requested. Colors ride the CSS design
// tokens (see styles.css `[data-skin]` blocks); this module carries the text/vocabulary layer.
//
// This proves the SEAM + the color expression. Full domain-vocabulary coverage across every
// panel, and a per-org persisted theme (an `org.theme` column returned by /api/me), are a
// documented follow-up — not attempted here (they need Nick's vocab-map + ski-brand sign-off).

export type SkinId = 'camp' | 'ski';

export interface Skin {
  id: SkinId;
  /** Product name shown in the chrome. */
  label: string;
  /** Small-caps line under the wordmark. */
  sub: string;
  /** domain term → vertical term. Empty for camp (the canonical vocabulary). */
  vocab: Record<string, string>;
}

const SKINS: Record<SkinId, Skin> = {
  camp: { id: 'camp', label: 'Tavezio Core', sub: 'Operations Console', vocab: {} },
  ski: {
    id: 'ski',
    label: 'Alpine Ops',
    sub: 'Resort Operations',
    // Provisional map — the demo default; Nick owns the final ski vocabulary.
    vocab: {
      camp: 'resort', camper: 'guest', session: 'trip', sessions: 'trips',
      roster: 'manifest', cabin: 'chalet', counselor: 'guide', enroll: 'book',
    },
  },
};

function readSkin(): Skin {
  try {
    const id = new URLSearchParams(window.location.search).get('skin');
    if (id && id in SKINS) return SKINS[id as SkinId];
  } catch { /* no window (SSR/tests) — fall through to camp */ }
  return SKINS.camp;
}

// The skin is fixed for the page load (chosen by the URL), so a module-level singleton keeps the
// proof free of prop/context threading — components read ACTIVE_SKIN or call term() directly.
export const ACTIVE_SKIN: Skin = readSkin();

// term('Roster') → 'Manifest' under the ski skin, 'Roster' under camp. Leading-capital preserving,
// and plural-aware: a word not in the map whose singular IS mapped is pluralised ('cabins' →
// 'chalets'), so the vocab map only needs singular entries.
export function term(word: string): string {
  const lower = word.toLowerCase();
  let mapped = ACTIVE_SKIN.vocab[lower];
  if (!mapped && lower.endsWith('s') && ACTIVE_SKIN.vocab[lower.slice(0, -1)]) {
    mapped = `${ACTIVE_SKIN.vocab[lower.slice(0, -1)]}s`;
  }
  if (!mapped) return word;
  return word[0] === word[0]?.toUpperCase() ? mapped[0].toUpperCase() + mapped.slice(1) : mapped;
}

// Apply term() to every word of a (possibly multi-word) label — for data-driven strings where
// wrapping individual words at the source isn't practical ('Total Campers' → 'Total Guests').
// No-op under the camp skin, so the default is untouched.
export function vocabize(text: string): string {
  if (!text || ACTIVE_SKIN.id === 'camp') return text;
  return text.replace(/[A-Za-z]+/g, (w) => term(w));
}
